import { UseControllerProps } from 'react-hook-form';
import { isAfter, isBefore, isValid, subYears } from 'date-fns';
import {
  INVALID_HEALOW_NAME_MESSAGE,
  isAValidHealowNameCallback,
} from '../../../helpers/validation';
import {
  VALID_EMAIL,
  VALID_PHONE_WITH_OPTIONAL_CHARACTERS,
  VALID_ZIPCODE,
} from '../../../helpers/const';

const isValidBirthdayFormat = (input: string) =>
  isValid(new Date(input)) && isAfter(new Date(input), new Date(1900, 1, 1));

const isAtLeastFiveYearsOld = (input: string) =>
  isBefore(new Date(input), subYears(new Date(), 5));

export const isAtLeastEighteenYearsOld = (input: string) =>
  isAfter(new Date(input), subYears(new Date(), 18));

export const rules: Record<string, UseControllerProps['rules']> = {
  requiredCheckboxOrRadio: {
    required: {
      value: true,
      message: 'Please make a selection.',
    },
  },

  firstName: {
    required: {
      value: true,
      message: 'Please enter a first name.',
    },
    validate: value =>
      isAValidHealowNameCallback(value) || INVALID_HEALOW_NAME_MESSAGE,
  },

  lastName: {
    required: {
      value: true,
      message: 'Please enter a last name.',
    },
    validate: value =>
      isAValidHealowNameCallback(value) || INVALID_HEALOW_NAME_MESSAGE,
  },

  preferredName: {
    required: {
      value: true,
      message: 'Please enter a preferred name.',
    },
    validate: value =>
      isAValidHealowNameCallback(value) || INVALID_HEALOW_NAME_MESSAGE,
  },

  birthDate: {
    required: {
      value: true,
      message: 'Please enter a birth date.',
    },
    validate: {
      validBirthday: value =>
        isValidBirthdayFormat(value) || 'Please enter a valid birthday.',
      fiveAndUp: value =>
        isAtLeastFiveYearsOld(value) ||
        'We only treat patients who are at least 5 years old.',
    },
  },

  phone: {
    required: {
      value: true,
      message: 'Please enter a phone number.',
    },
    pattern: {
      value: VALID_PHONE_WITH_OPTIONAL_CHARACTERS,
      message: 'Please enter a valid phone number.',
    },
  },

  email: {
    required: {
      value: true,
      message: 'Please enter an email address.',
    },
    pattern: {
      value: VALID_EMAIL,
      message: 'Please enter a valid email address.',
    },
  },

  sex: {
    required: {
      value: true,
      message: 'Please enter a sex matching your insurance provider.',
    },
  },

  addressStreet1: {
    required: {
      value: true,
      message: 'Please enter a street address.',
    },
  },

  addressCity: {
    required: {
      value: true,
      message: 'Please enter a city.',
    },
  },

  addressState: {
    required: {
      value: true,
      message: 'Please select a state.',
    },
  },

  addressZipCode: {
    required: {
      value: true,
      message: 'Please enter a ZIP code.',
    },
    pattern: {
      value: VALID_ZIPCODE,
      message: 'Please enter a valid ZIP code.',
    },
  },

  addressZipCodeOptional: {
    pattern: {
      value: VALID_ZIPCODE,
      message: 'Please enter a valid ZIP code.',
    },
  },

  relationToPatient: {
    required: {
      value: true,
      message: 'Please select a relation.',
    },
  },

  customRelation: {
    required: {
      value: true,
      message: 'Please enter a relation.',
    },
  },

  consentForMinor: {
    required: {
      value: true,
      message: 'You must consent to this question to continue.',
    },
  },

  isPolicyAcknowledged: {
    required: {
      value: true,
      message: 'You must agree to the terms before continuing.',
    },
    validate: value => value === true,
  },

  insuranceCarrier: {
    required: {
      value: true,
      message: 'Please select your insurance carrier.',
    },
  },

  insurancePlan: {
    required: {
      value: true,
      message: 'Please select your plan.',
    },
  },

  insuranceId: {
    required: {
      value: true,
      message: 'Please enter your member ID.',
    },
  },

  medications: {
    required: {
      value: true,
      message: 'Please enter one or more medications.',
    },
  },

  pharmacyName: {
    required: {
      value: true,
      message: 'Please enter a pharmacy name.',
    },
  },

  policyholder: {
    required: {
      value: true,
      message: 'Please select a policyholder.',
    },
  },

  practiceName: {
    required: {
      value: true,
      message: 'Please enter your practice name.',
    },
  },

  practiceType: {
    required: {
      value: true,
      message: 'Please enter your practice type.',
    },
  },
};
